import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ApplicationState } from 'store';
import {
  createProcess,
  clearQuotesByCompany,
  createQuoteJunto,
  createQuotePottencial,
  createQuoteSombrero,
  createQuoteAvla,
} from 'store/modules/conlicitaseg_store/actions';
import { ConlicitasegState } from 'store/modules/conlicitaseg_store/types';
import { ProfileState } from 'store/modules/profile_store/types';
import InsuranceStepIndicator from 'components/atoms/insurance_step_indicator';
import useQuoteFlow from 'hooks/useQuoteFlow';
import { formatCurrency, formatCNPJ, formatDate, getInsuranceReason } from 'services/conlicitaseg';
import LoadingBidding from '../loading_bidding';
import { LoadingContainer, StateIcon } from './styles';

const QuoteConfirmation: React.FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const shouldCreateQuote = useRef(false);

  const { formProcess, request, current_process_id, origin_channel } = useSelector<
    ApplicationState,
    ConlicitasegState
  >((state) => state.session.conlicitaseg);

  const { pathFor, stepIndicatorProps } = useQuoteFlow();

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const [submitted, setSubmitted] = useState(false);

  const handleConfirm = () => {
    shouldCreateQuote.current = true;
    setSubmitted(true);
    dispatch(createProcess({ ...formProcess, origin_channel }));
  };

  useEffect(() => {
    if (shouldCreateQuote.current && formProcess && current_process_id) {
      dispatch(clearQuotesByCompany());
      dispatch(createQuoteJunto(formProcess, profile.id, profile.clientId, current_process_id));
      dispatch(
        createQuotePottencial(formProcess, profile.id, profile.clientId, current_process_id),
      );
      dispatch(createQuoteSombrero(formProcess, profile.id, profile.clientId, current_process_id));
      dispatch(createQuoteAvla(formProcess, profile.id, profile.clientId, current_process_id));
      shouldCreateQuote.current = false;
      history.push(`/conlicitaseg/cotacoes/${current_process_id}`);
    }
  }, [current_process_id]);

  if (submitted && request?.create_process?.loading) {
    return (
      <div className="page-container">
        <div className="container py-5">
          <div className="bg-white rounded shadow-sm p-4">
            <LoadingContainer>
              <LoadingBidding
                title="Por favor, aguarde…"
                subtitle="Estamos buscando a melhor cotação para sua empresa!"
              />
            </LoadingContainer>
          </div>
        </div>
      </div>
    );
  }

  if (submitted && request?.create_process?.error) {
    return (
      <div className="page-container">
        <div className="container py-5">
          <div className="bg-white rounded shadow-sm p-4 d-flex flex-column align-items-center text-center">
            <StateIcon
              className="material-icons-outlined text-dark"
              aria-label="Erro ao confirmar proposta."
            >
              assignment_late
            </StateIcon>
            <h3>Não foi possível confirmar sua proposta.</h3>
            <p>Ocorreu um erro ao enviar seus dados. Tente novamente.</p>
            <button type="button" className="btn btn-primary" onClick={handleConfirm}>
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container-fluid big-resolutions py-5 px-md-5">
        <div className="row justify-content-center mt-5">
          <div className="col-12 mx-auto">
            <div className="row align-items-stretch">
              <div className="col-md-9">
                <div className="shadow-sm p-4 bg-white rounded">
                  <h3 className="mb-2 text-dark">Confirme os dados antes de continuar</h3>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <div className="border p-3 rounded h-100">
                        <div className="mb-3">
                          <strong>Motivo do seguro</strong>
                          <div className="text-secondary">
                            {getInsuranceReason(formProcess.type_warranty_id || '')}
                          </div>
                        </div>

                        <div className="mb-3">
                          <strong>Empresa que está contratando o seguro (Tomador)</strong>
                          <div className="text-secondary">
                            {formatCNPJ(formProcess.policyholder_cnpj)} -{' '}
                            {formProcess.policyholder_company_name}
                          </div>
                        </div>

                        <div className="mb-3">
                          <strong>Órgão público que solicitou o seguro (Segurado)</strong>
                          <div className="text-secondary">
                            {formatCNPJ(formProcess.insured_cnpj)} - {formProcess.insured_name}
                          </div>
                          <div className="text-secondary">
                            {`${formProcess.insured_address}, ${formProcess.insured_number}, ${formProcess.insured_district}, ${formProcess.insured_city}/${formProcess.insured_state}, ${formProcess.insured_zip_code}`}
                          </div>
                        </div>

                        <div className="mb-3">
                          <strong>Modalidade da licitação</strong>
                          <div className="text-secondary">{formProcess.bidding_modality}</div>
                        </div>

                        <div className="mb-3">
                          <strong>N° do edital</strong>
                          <div className="text-secondary">{formProcess.bidding_edict}</div>
                        </div>

                        {formProcess.bidding_contract_number && (
                          <div className="mb-3">
                            <strong>N° do contrato</strong>
                            <div className="text-secondary">
                              {formProcess.bidding_contract_number}
                            </div>
                          </div>
                        )}

                        {formProcess.bidding_process_number && (
                          <div className="mb-3">
                            <strong>N° do processo</strong>
                            <div className="text-secondary">
                              {formProcess.bidding_process_number}
                            </div>
                          </div>
                        )}

                        <div className="mb-3">
                          <strong>Vigência do seguro</strong>
                          <div className="text-secondary">
                            {formatDate(formProcess.contract_start_date)} até{' '}
                            {formatDate(formProcess.contract_end_date)} (
                            {formProcess.contract_days || 0} dias)
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6 mt-3 mt-md-0">
                      <div className="border p-3 rounded h-100">
                        <div className="mb-3">
                          <strong>
                            {formProcess.type_warranty_id === 'Licitante'
                              ? 'Valor estimado'
                              : 'Valor do contrato'}
                          </strong>
                          <div className="text-secondary">
                            {formatCurrency(formProcess.contract_value || 0, { emptyOnZero: true })}
                          </div>
                        </div>

                        <div className="mb-3">
                          <strong>Porcentagem da garantia</strong>
                          <div className="text-secondary">
                            {formProcess.contract_percentage || 0}%
                          </div>
                        </div>

                        {!!formProcess.contract_additional_value && (
                          <div className="mb-3">
                            <strong>Valor da Garantia Adicional</strong>
                            <div className="text-secondary">
                              {formatCurrency(formProcess.contract_additional_value, {
                                emptyOnZero: true,
                              })}
                            </div>
                          </div>
                        )}

                        <div className="mb-3">
                          <strong>Valor total da cobertura</strong>
                          <div className="text-secondary">
                            {formatCurrency(formProcess.contract_insured_amount || 0, {
                              emptyOnZero: true,
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-end align-items-center">
                    <button
                      type="button"
                      className="btn btn-link mr-2"
                      onClick={() => history.push(pathFor('licitacao'))}
                    >
                      Voltar etapa
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => history.push('/conlicitaseg')}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary ml-2"
                      onClick={handleConfirm}
                      disabled={request?.create_process?.loading}
                    >
                      Continuar
                    </button>
                  </div>
                </div>
              </div>
              <div className="col-md-3 d-none d-md-block">
                <InsuranceStepIndicator {...stepIndicatorProps('confirmacao')} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteConfirmation;
